import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell as BellIcon } from 'lucide-react'
import { Icon } from './Icon'
import { api, post } from '../lib/api'
import { ago } from '../lib/format'

type Notice = { id: string; kind: string; title: string; body: string | null; payload: { url?: string; assignment_id?: string } | null; read_at: string | null; created_at: string }

const TONE: Record<string, string> = { escalate: 'neg', mailbox: 'neg', email_fail: 'neg', rep_quiet: 'warn', held: 'warn', remind: 'warn' }

/**
 * Everything the watcher and routing sent this person, in the app. Phone alerts
 * are the fast path; this is the one that is always there, even without them.
 */
export default function Bell() {
  const [open, setOpen] = useState(false)
  const [data, setData] = useState<{ notifications: Notice[]; unread: number } | null>(null)
  const nav = useNavigate()
  const box = useRef<HTMLDivElement>(null)

  const load = () => api<{ notifications: Notice[]; unread: number }>('/api/notifications').then(setData).catch(() => {})
  useEffect(() => {
    load()
    const t = setInterval(load, 60_000)
    const onFocus = () => load()
    window.addEventListener('focus', onFocus)
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus) }
  }, [])
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [open])

  const go = async (n: Notice) => {
    setOpen(false)
    if (!n.read_at) await post('/api/notifications/read', { ids: [n.id] }).catch(() => {})
    const url = n.payload?.url || (n.payload?.assignment_id ? `/my/${n.payload.assignment_id}` : null)
    if (url) nav(url)
    load()
  }
  const readAll = async () => { await post('/api/notifications/read', {}).catch(() => {}); load() }
  const unread = data?.unread || 0

  return (
    <div className="bell" ref={box}>
      <button className="btn btn-ghost bell-btn" aria-label={unread ? `${unread} unread notices` : 'Notices'} aria-expanded={open} onClick={() => setOpen(!open)}>
        <Icon of={BellIcon} />
        {unread > 0 && <span className="bell-count num">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="bell-panel" role="dialog" aria-label="Notices">
          <div className="bell-head">
            <strong>Notices</strong>
            {unread > 0 && <button className="btn btn-ghost" onClick={readAll}>Mark all read</button>}
          </div>
          {!data?.notifications.length ? <p className="muted bell-empty">Nothing yet. Stuck leads, quiet reps and held alerts show up here.</p> : (
            <ul className="bell-list">
              {data.notifications.map((n) => (
                <li key={n.id}>
                  <button className={`bell-item${n.read_at ? '' : ' unread'} tone-${TONE[n.kind] || 'none'}`} onClick={() => go(n)}>
                    <span className="bell-title">{n.title}</span>
                    {n.body && <span className="bell-body">{n.body}</span>}
                    <span className="bell-when muted">{ago(n.created_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
